import { useMemo, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectBannerData } from '~store/app/slice';
import { API_BANNER_IMPRESSION_BJ } from '~global-api/bj.api';

const BANNER_STORAGE_KEY = 'banners';
const SESSION_STORAGE_KEY = 'banner_session_impressions';

// Track which banners we've already impressed during this page load
const alreadyImpressedBanners = new Set();

const getBannerImpressions = (isSessionType = false) => {
  try {
    const storage = isSessionType ? sessionStorage : localStorage;
    const key = isSessionType ? SESSION_STORAGE_KEY : BANNER_STORAGE_KEY;
    return JSON.parse(storage.getItem(key) || '[]');
  } catch {
    return [];
  }
};

const updateBannerImpression = (bannerId, isSessionType = false) => {
  const impressions = getBannerImpressions(isSessionType);
  const existing = impressions.find((i) => i.banner_id === bannerId);

  if (existing) {
    existing.impressionCount += 1;
  } else {
    impressions.push({ banner_id: bannerId, impressionCount: 1 });
  }

  const storage = isSessionType ? sessionStorage : localStorage;
  const key = isSessionType ? SESSION_STORAGE_KEY : BANNER_STORAGE_KEY;
  storage.setItem(key, JSON.stringify(impressions));
};

const cleanupStaleImpressions = (bannerType, allBanners) => {
  // Filter active banner IDs to only include banners of the same type
  const activeBannerIdsOfType = allBanners
    .filter((banner) => banner.type === bannerType)
    .map((banner) => banner._id);

  // Clean up localStorage
  const localImpressions = getBannerImpressions(false);
  const validLocalImpressions = localImpressions.filter((imp) => {
    // Keep impressions for other banner types
    const banner = allBanners.find((b) => b._id === imp.banner_id);
    if (banner?.type !== bannerType) {
      return true;
    }
    // For current banner type, only keep active banner impressions
    return activeBannerIdsOfType.includes(imp.banner_id);
  });
  localStorage.setItem(
    BANNER_STORAGE_KEY,
    JSON.stringify(validLocalImpressions),
  );

  // Clean up sessionStorage
  const sessionImpressions = getBannerImpressions(true);
  const validSessionImpressions = sessionImpressions.filter((imp) => {
    // Keep impressions for other banner types
    const banner = allBanners.find((b) => b._id === imp.banner_id);
    if (banner?.type !== bannerType) {
      return true;
    }
    // For current banner type, only keep active banner impressions
    return activeBannerIdsOfType.includes(imp.banner_id);
  });
  sessionStorage.setItem(
    SESSION_STORAGE_KEY,
    JSON.stringify(validSessionImpressions),
  );
};

export const useBanner = ({ bannerType, location }) => {
  const bannerData = useSelector(selectBannerData);
  const [initiallyValidBannerIds, setInitiallyValidBannerIds] = useState([]);

  // Clean up stale impressions whenever bannerData changes
  useEffect(() => {
    if (bannerData) {
      cleanupStaleImpressions(bannerType, bannerData);
    }
  }, [bannerData, bannerType]);

  // Function to handle when a banner becomes visible
  const handleBannerVisible = useCallback((bannerItem) => {
    if (!bannerItem || !bannerItem._id) {
      return;
    }

    const bannerId = bannerItem._id;

    // Only track impression if this banner hasn't been impressed yet during this page load
    if (!alreadyImpressedBanners.has(bannerId)) {
      alreadyImpressedBanners.add(bannerId);

      const isSessionType =
        bannerItem.metadata_impressions_cap_type === 'PER_SESSION';
      updateBannerImpression(bannerId, isSessionType);
      API_BANNER_IMPRESSION_BJ(bannerId).catch(console.error);
    }
  }, []);

  const validBanner = useMemo(() => {
    const currentPath = window.location.pathname;
    const currentHostname = window.location.hostname;

    let bannersOfType = bannerData?.filter((e) => e.type === bannerType);
    // Filter by location if provided
    if (location && bannersOfType) {
      // Handle for balloon-ads on list quiz pages
      const currentPath = window.location.pathname;
      if (currentPath == '/whats-fun/quizzes-poll') {
        location = 'quiz-pages';
      }
      const handleCategoryPagesLocation = (banner, location) => {
        const specificCategories = /whats-new|whats-fun|whats-good|beauty-az/;
        if (
          banner.location == 'category-pages' &&
          specificCategories.test(location)
        ) {
          return true;
        }
      };
      bannersOfType = bannersOfType.filter((banner) => {
        return (
          banner.location?.includes(location) ||
          banner.location?.includes('all-pages') ||
          handleCategoryPagesLocation(banner, location)
        );
      });
    }

    // Get initial set of valid banners based on impression caps and link
    const fullyValidatedBannersOfType = bannersOfType?.filter((banner) => {
      if (banner.ad_source === 'AdSense') {
        return true;
      }
      // Filter out banners that link to current path on the same domain
      if (banner.link) {
        try {
          const bannerUrl = new URL(banner.link);
          const bannerPath = bannerUrl.pathname;
          const bannerHostname = bannerUrl.hostname;

          if (
            bannerHostname === currentHostname &&
            bannerPath === currentPath
          ) {
            return false;
          }
        } catch (error) {
          console.error('Invalid banner link URL:', error);
        }
      }

      // include the banner if it was in the initial set, regardless of impression count
      if (initiallyValidBannerIds.includes(banner._id)) {
        return true;
      }

      // Skip impression cap check if not enabled or if it's an AdSense banner
      if (!banner.metadata_impression_cap_enabled) {
        return true;
      }

      const isSessionType =
        banner.metadata_impressions_cap_type === 'PER_SESSION';
      const impressions = getBannerImpressions(isSessionType);
      const bannerImpressions =
        impressions.find((i) => i.banner_id === banner._id)?.impressionCount ||
        0;
      return bannerImpressions < banner.metadata_impressions_cap;
    });

    // For single main banner, track impression immediately, since no other banners will hide it from carousel
    if (
      fullyValidatedBannersOfType?.length === 1 &&
      bannerType === 'top-banner' &&
      fullyValidatedBannersOfType[0].ad_source === 'Direct'
    ) {
      const singleTopBannerForImpression = fullyValidatedBannersOfType[0];
      handleBannerVisible(singleTopBannerForImpression);
    }

    // Store the IDs of valid banners for later comparison
    if (
      fullyValidatedBannersOfType?.length > 0 &&
      initiallyValidBannerIds.length === 0
    ) {
      setInitiallyValidBannerIds(
        fullyValidatedBannersOfType.map((banner) => banner._id),
      );
    }

    if (['parallax-banner', 'hidden-quiz-banner'].includes(bannerType)) {
      if (fullyValidatedBannersOfType?.length > 0) {
        return fullyValidatedBannersOfType[0];
      }
      return null;
    }

    // Return the first valid banner, sorted by start date
    return fullyValidatedBannersOfType?.sort(
      (a, b) => new Date(a.start_date) - new Date(b.start_date),
    );
  }, [
    bannerData,
    bannerType,
    location,
    handleBannerVisible,
    initiallyValidBannerIds,
  ]);

  return { validBanner, handleBannerVisible };
};
